import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const SHOW_DELAY_MS = 450;
/** After a tooltip closes, others open instantly for this long (scanning a toolbar). */
const WARM_WINDOW_MS = 400;
const GAP = 8;
const MARGIN = 8;
const TOOLTIP_ID = 'app-tooltip';

interface Shown {
  target: HTMLElement;
  text: string;
  shortcut?: string;
}

function tooltipTarget(node: EventTarget | null): HTMLElement | null {
  if (!(node instanceof Element)) return null;
  const el = node.closest<HTMLElement>('[data-tooltip], [title]');
  if (!el) return null;
  // Promote native titles so the browser's own tooltip never shows alongside ours.
  const title = el.getAttribute('title');
  if (title !== null) {
    el.removeAttribute('title');
    if (title && !el.dataset.tooltip) el.dataset.tooltip = title;
  }
  return el.dataset.tooltip ? el : null;
}

/**
 * Renders the application's tooltips. Mount once. Shows for hovered or keyboard-focused elements
 * carrying `data-tooltip` (or a legacy `title`), positions above the target (flipping below when
 * there is no room), and hides on interaction, scroll or when the target disappears.
 */
export function TooltipHost() {
  const [shown, setShown] = useState<Shown | null>(null);
  const [pos, setPos] = useState<{
    left: number;
    top: number;
    placement: 'top' | 'bottom';
    arrow: number;
  } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let current: HTMLElement | null = null;
    let lastHide = 0;

    const clear = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const hide = () => {
      clear();
      if (current) {
        current.removeAttribute('aria-describedby');
        lastHide = performance.now();
      }
      current = null;
      setShown(null);
    };
    const show = (el: HTMLElement) => {
      current = el;
      el.setAttribute('aria-describedby', TOOLTIP_ID);
      setShown({ target: el, text: el.dataset.tooltip ?? '', shortcut: el.dataset.tooltipShortcut });
    };
    const schedule = (el: HTMLElement) => {
      if (el === current) return;
      if (current) hide();
      clear();
      const warm = performance.now() - lastHide < WARM_WINDOW_MS;
      if (warm) show(el);
      else timer = setTimeout(() => show(el), SHOW_DELAY_MS);
    };

    // pointermove (not pointerover) so tooltips also work on disabled buttons, which don't
    // receive pointer events in every browser: we hit-test the point ourselves.
    const onMove = (e: PointerEvent) => {
      if (e.buttons !== 0 || e.pointerType === 'touch') return;
      const el = tooltipTarget(document.elementFromPoint(e.clientX, e.clientY));
      if (el) schedule(el);
      else if (current || timer) hide();
    };
    const onFocusIn = (e: FocusEvent) => {
      const el = tooltipTarget(e.target);
      if (el && el.matches(':focus-visible')) schedule(el);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || !e.key.startsWith('Tab')) hide();
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', hide, true);
    window.addEventListener('wheel', hide, { passive: true, capture: true });
    window.addEventListener('scroll', hide, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', hide);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', hide);
    document.documentElement.addEventListener('pointerleave', hide);
    // Hide if the target is removed from the DOM (e.g. a dialog closed under the pointer).
    const watchdog = setInterval(() => current && !current.isConnected && hide(), 250);
    return () => {
      clear();
      clearInterval(watchdog);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', hide, true);
      window.removeEventListener('wheel', hide, true);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', hide);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', hide);
      document.documentElement.removeEventListener('pointerleave', hide);
    };
  }, []);

  useLayoutEffect(() => {
    const tipEl = ref.current;
    if (!shown || !tipEl) {
      setPos(null);
      return;
    }
    const t = shown.target.getBoundingClientRect();
    const { width, height } = tipEl.getBoundingClientRect();
    const placement = t.top - height - GAP < MARGIN ? 'bottom' : 'top';
    const top = placement === 'top' ? t.top - height - GAP : t.bottom + GAP;
    const centre = t.left + t.width / 2;
    const left = Math.min(window.innerWidth - width - MARGIN, Math.max(MARGIN, centre - width / 2));
    setPos({ left, top, placement, arrow: Math.min(width - 10, Math.max(10, centre - left)) });
  }, [shown]);

  if (!shown) return null;
  return (
    <div
      ref={ref}
      id={TOOLTIP_ID}
      role="tooltip"
      className={`tooltip ${pos?.placement ?? 'top'}${pos ? ' visible' : ''}`}
      style={
        pos
          ? { left: pos.left, top: pos.top, ['--arrow' as string]: `${pos.arrow}px` }
          : { left: -9999, top: -9999 }
      }
    >
      <span>{shown.text}</span>
      {shown.shortcut && <kbd className="tooltip-kbd">{shown.shortcut}</kbd>}
    </div>
  );
}
